import React, { useState } from 'react';
import PropTypes from 'prop-types';
import HtmlEmailDisplay from './HtmlEmailDisplay';
import PlainTextEmailDisplay from './PlainTextEmailDisplay';

const EmailBodyRenderer = ({ htmlContent, plainTextContent }) => {
    const [viewMode, setViewMode] = useState('html');

    if (!htmlContent && !plainTextContent) {
        return (
            <div style={{
                padding: '40px',
                textAlign: 'center',
                color: '#94a3b8',
                background: '#f8fafc',
                borderRadius: '12px',
                border: '1px dashed #e2e8f0'
            }}>
                <div style={{ fontSize: '32px', marginBottom: '12px' }}>📭</div>
                <div style={{ fontSize: '14px', fontWeight: '500' }}>No email content available</div>
            </div>
        );
    }

    const getPlainText = () => {
        if (plainTextContent) return plainTextContent;

        if (htmlContent) {
            try {
                const doc = new DOMParser().parseFromString(htmlContent, 'text/html');
                return doc.body.textContent || "No plain text available";
            } catch (e) {
                console.error("Error extracting plain text from HTML:", e);
                return "Error extracting plain text";
            }
        }

        return "No content available";
    };

    const hasHtmlContent = Boolean(htmlContent && /<[a-z][\s\S]*>/i.test(htmlContent));

    return (
        <div className="email-body-renderer" style={{
            background: '#ffffff',
            borderRadius: '16px',
            border: '1px solid #e5e7eb',
            overflow: 'hidden',
            boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.05), 0 2px 4px -1px rgba(0, 0, 0, 0.03)'
        }}>
            {/* Modern Tab Bar */}
            <div style={{
                display: 'flex',
                alignItems: 'center',
                gap: '4px',
                padding: '12px 16px',
                background: 'linear-gradient(to bottom, #f9fafb, #f3f4f6)',
                borderBottom: '1px solid #e5e7eb'
            }}>
                {hasHtmlContent && (
                    <button
                        onClick={() => setViewMode('html')}
                        style={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: '8px',
                            padding: '10px 20px',
                            border: 'none',
                            borderRadius: '10px',
                            cursor: 'pointer',
                            fontSize: '14px',
                            fontWeight: '600',
                            fontFamily: 'inherit',
                            transition: 'all 0.2s ease',
                            background: viewMode === 'html'
                                ? 'linear-gradient(135deg, #F5A623 0%, #f59e0b 100%)'
                                : '#ffffff',
                            color: viewMode === 'html' ? '#ffffff' : '#64748b',
                            boxShadow: viewMode === 'html'
                                ? '0 4px 12px rgba(245, 166, 35, 0.3)'
                                : '0 1px 3px rgba(0, 0, 0, 0.08)',
                            transform: viewMode === 'html' ? 'scale(1.02)' : 'scale(1)'
                        }}
                        title="View as rendered HTML"
                    >
                        <span style={{ fontSize: '16px' }}>🌐</span>
                        <span>Web View</span>
                    </button>
                )}
                <button
                    onClick={() => setViewMode('text')}
                    style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: '8px',
                        padding: '10px 20px',
                        border: 'none',
                        borderRadius: '10px',
                        cursor: 'pointer',
                        fontSize: '14px',
                        fontWeight: '600',
                        fontFamily: 'inherit',
                        transition: 'all 0.2s ease',
                        background: viewMode === 'text'
                            ? 'linear-gradient(135deg, #F5A623 0%, #f59e0b 100%)'
                            : '#ffffff',
                        color: viewMode === 'text' ? '#ffffff' : '#64748b',
                        boxShadow: viewMode === 'text'
                            ? '0 4px 12px rgba(245, 166, 35, 0.3)'
                            : '0 1px 3px rgba(0, 0, 0, 0.08)',
                        transform: viewMode === 'text' ? 'scale(1.02)' : 'scale(1)'
                    }}
                    title="View as plain text"
                >
                    <span style={{ fontSize: '16px' }}>📄</span>
                    <span>Plain Text</span>
                </button>

                {/* Spacer */}
                <div style={{ flex: 1 }} />

                {/* View indicator */}
                <div style={{
                    fontSize: '12px',
                    color: '#9ca3af',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '6px'
                }}>
                    <span style={{
                        width: '8px',
                        height: '8px',
                        borderRadius: '50%',
                        background: viewMode === 'html' ? '#22c55e' : '#3b82f6'
                    }} />
                    {viewMode === 'html' ? 'Rendered HTML' : 'Text Mode'}
                </div>
            </div>

            {/* Content Area */}
            <div style={{
                background: viewMode === 'html' ? '#ffffff' : '#f8fafc'
            }}>
                {viewMode === 'html' && hasHtmlContent ? (
                    <HtmlEmailDisplay htmlContent={htmlContent} />
                ) : (
                    <PlainTextEmailDisplay textContent={getPlainText()} />
                )}
            </div>
        </div>
    );
};

EmailBodyRenderer.propTypes = {
    htmlContent: PropTypes.string,
    plainTextContent: PropTypes.string
};

export default EmailBodyRenderer;
